/**
 * URL Helper Functions for SEO-friendly hierarchical URLs
 */

/**
 * Convert a string to a URL-friendly slug
 * Examples:
 * - "New Delhi" -> "new-delhi"
 * - "Andheri West" -> "andheri-west"
 * - "AC Repair" -> "ac-repair"
 */
export const toSlug = (text: string): string => {
  if (!text) return "";
  return text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/[^\w-]+/g, "") // Remove non-word chars except hyphens
    .replace(/--+/g, "-") // Replace multiple hyphens with single hyphen
    .replace(/^-+/, "") // Trim hyphens from start
    .replace(/-+$/, ""); // Trim hyphens from end
};

/**
 * Convert a slug back to a readable string
 * Examples:
 * - "new-delhi" -> "New Delhi"
 * - "andheri-west" -> "Andheri West"
 * - "ac-repair" -> "Ac Repair"
 */
export const fromSlug = (slug: string): string => {
  if (!slug) return "";
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Build a search URL following the pattern: /{keyword}-in-{location}
 * @param city - City name (required)
 * @param locality - Locality name (optional)
 * @param keyword - Search keyword (optional)
 * @returns SEO-friendly URL path
 *
 * Examples:
 * - buildSearchUrl("Kakinada", "", "") -> "/kakinada"
 * - buildSearchUrl("Kakinada", "", "plumber") -> "/plumber-in-kakinada"
 * - buildSearchUrl("Mumbai", "Andheri", "") -> "/mumbai-andheri"
 * - buildSearchUrl("Mumbai", "Andheri", "plumber") -> "/plumber-in-mumbai-andheri"
 */
export const buildSearchUrl = (
  city: string,
  locality?: string,
  keyword?: string,
): string => {
  const citySlug = toSlug(city);

  if (!citySlug) {
    return "/";
  }

  // Build the location slug: city-locality (if locality present) or just city
  let locationSlug = citySlug;
  if (locality) {
    const localitySlug = toSlug(locality);
    locationSlug = `${citySlug}-${localitySlug}`;
  }

  // If we have a keyword, produce /{keyword}-in-{location}
  if (keyword) {
    const keywordSlug = toSlug(keyword);
    return `/${keywordSlug}-in-${locationSlug}`;
  }

  // If we only have location (city or city+locality)
  return `/${locationSlug}`;
};

/**
 * Parse URL parameters and decode them.
 * Handles the URL patterns:
 * - /{city}                      (city-only, no keyword)
 * - /{keyword}-in-{city}         (keyword + city)
 * - /{keyword}-in-{city}-{locality} (keyword + city + locality)
 *
 * @param params - URL parameters from useParams — only `location` is used now;
 *                 the `keyword` field is kept for backwards-compatibility but ignored.
 * @param cities - List of available cities to match against
 * @returns Decoded parameters with separated city, locality, and keyword
 */
export const parseSearchParams = (
  params: {
    location?: string;
    keyword?: string;
  },
  cities?: Array<{ city: string; hasLocality?: boolean }>,
): {
  city: string;
  locality: string;
  keyword: string;
} => {
  if (!params.location) {
    return { city: "", locality: "", keyword: "" };
  }

  const locationDecoded = decodeURIComponent(params.location).toLowerCase();

  // Split on the literal "-in-" separator to extract keyword from location slug.
  // e.g. "plumber-in-andheri-kakinada" -> keyword="plumber", location="andheri-kakinada"
  // e.g. "kakinada" -> no separator -> keyword="", location="kakinada"
  const inIndex = locationDecoded.indexOf("-in-");
  let keywordSlug = "";
  let locationSlug = locationDecoded;

  if (inIndex !== -1) {
    keywordSlug = locationDecoded.substring(0, inIndex);
    locationSlug = locationDecoded.substring(inIndex + 4); // skip "-in-"
  }

  const keyword = keywordSlug ? fromSlug(keywordSlug) : "";

  // Try to match the location slug against known cities.
  // The location slug may be "{city}" or "{city}-{locality}".
  if (cities && cities.length > 0) {
    for (const cityObj of cities) {
      const citySlug = toSlug(cityObj.city);

      // Location slug ends with the city slug
      if (locationSlug === citySlug) {
        // Exact city match — no locality
        return { city: cityObj.city, locality: "", keyword };
      }

      if (locationSlug.startsWith(`${citySlug}-`)) {
        // Locality is everything after "{citySlug}-"
        const localitySlug = locationSlug.slice(citySlug.length + 1);
        return { city: cityObj.city, locality: fromSlug(localitySlug), keyword };
      }
    }
  }

  // Fallback: treat the whole location slug as city
  return { city: fromSlug(locationSlug), locality: "", keyword };
};

// Made with Bob
